import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const getTempWarehouseProducts = createAsyncThunk(
    'tempWarehouse/getTempWarehouseProducts',
    async ({ accountId, token, search, start, length, warehouseId, warehousePositionId }, { rejectWithValue }) => {
    try {
        let url = `${import.meta.env.VITE_API_BASE_URL}/products/list?accountId=${accountId}&start=${start}&length=${length}&draw=1&tempWarehouse=true`;
        
        // Aggiungo i parametri di ricerca se presenti
        if (search && search.length > 0) {
            url += `&search[value]=${encodeURIComponent(search)}`;
        } else {
            url += `&search[value]=`;
        }
        
        // Aggiungo filtri opzionali
        if (warehouseId) {
            url += `&warehouseId=${warehouseId}`;
        }
        
        if (warehousePositionId) {
            url += `&warehousePositionId=${warehousePositionId}`;
        }
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei prodotti in magazzino temporaneo');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const getWarehouses = createAsyncThunk(
    'tempWarehouse/getWarehouses',
    async ({ accountId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/warehouses?accountId=${accountId}`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei magazzini');
        }
        
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const getWarehousePositions = createAsyncThunk(
    'tempWarehouse/getWarehousePositions',
    async ({ warehouseId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/warehouses/positions?warehouseId=${warehouseId}`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero delle posizioni del magazzino');
        }
        
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const moveToMainWarehouse = createAsyncThunk(
    'tempWarehouse/moveToMainWarehouse',
    async ({ productId, variantId, warehouseId, warehousePositionId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/products/moveToWarehouse`;
        
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({
                productId,
                variantId,
                warehouseId,
                warehousePositionId
            })
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante lo spostamento del prodotto nel magazzino principale');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

const tempWarehouseSlice = createSlice({
    name: 'tempWarehouse',
    initialState: {
        data: null,
        loading: false,
        error: null,
        warehouses: [],
        loadingWarehouses: false,
        errorWarehouses: null,
        warehousePositions: [],
        loadingPositions: false,
        errorPositions: null,
        moveToWarehouseLoading: false,
        moveToWarehouseError: null
    },
    reducers: {
        resetWarehousePositions: (state) => {
            state.warehousePositions = [];
            state.loadingPositions = false;
            state.errorPositions = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // getTempWarehouseProducts
            .addCase(getTempWarehouseProducts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getTempWarehouseProducts.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(getTempWarehouseProducts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Errore durante il recupero dei prodotti in magazzino temporaneo';
            })
            // getWarehouses
            .addCase(getWarehouses.pending, (state) => {
                state.loadingWarehouses = true;
                state.errorWarehouses = null;
            })
            .addCase(getWarehouses.fulfilled, (state, action) => {
                state.loadingWarehouses = false;
                state.warehouses = action.payload;
            })
            .addCase(getWarehouses.rejected, (state, action) => {
                state.loadingWarehouses = false;
                state.errorWarehouses = action.payload || 'Errore durante il recupero dei magazzini';
            })
            // getWarehousePositions
            .addCase(getWarehousePositions.pending, (state) => {
                state.loadingPositions = true;
                state.errorPositions = null;
            })
            .addCase(getWarehousePositions.fulfilled, (state, action) => {
                state.loadingPositions = false;
                state.warehousePositions = action.payload;
            })
            .addCase(getWarehousePositions.rejected, (state, action) => {
                state.loadingPositions = false;
                state.errorPositions = action.payload || 'Errore durante il recupero delle posizioni del magazzino';
            })
            // moveToMainWarehouse
            .addCase(moveToMainWarehouse.pending, (state) => {
                state.moveToWarehouseLoading = true;
                state.moveToWarehouseError = null;
            })
            .addCase(moveToMainWarehouse.fulfilled, (state) => {
                state.moveToWarehouseLoading = false;
            })
            .addCase(moveToMainWarehouse.rejected, (state, action) => {
                state.moveToWarehouseLoading = false;
                state.moveToWarehouseError = action.payload || 'Errore durante lo spostamento del prodotto nel magazzino principale';
            });
    }
});

export const { resetWarehousePositions } = tempWarehouseSlice.actions;

export const selectTempWarehouseData = (state) => state.tempWarehouse.data;
export const selectTempWarehouseLoading = (state) => state.tempWarehouse.loading;
export const selectTempWarehouseError = (state) => state.tempWarehouse.error;
export const selectWarehouses = (state) => state.tempWarehouse.warehouses;
export const selectWarehousesLoading = (state) => state.tempWarehouse.loadingWarehouses;
export const selectWarehousesError = (state) => state.tempWarehouse.errorWarehouses;
export const selectWarehousePositions = (state) => state.tempWarehouse.warehousePositions;
export const selectWarehousePositionsLoading = (state) => state.tempWarehouse.loadingPositions;
export const selectWarehousePositionsError = (state) => state.tempWarehouse.errorPositions;
export const selectMoveToWarehouseLoading = (state) => state.tempWarehouse.moveToWarehouseLoading;
export const selectMoveToWarehouseError = (state) => state.tempWarehouse.moveToWarehouseError;

export default tempWarehouseSlice.reducer;
